import { useRouter } from 'found';
import styled from 'styled-components';
import React, { useState } from 'react';
import { makeMutation } from '../../../../api/helpers';
import { AddCurrency as AddCurrencyMutation } from '../../../../api/mutations';
import ContentCenterer from '../../../../components/ContentCenterer';
import Button from '../../../../components/input/Button';
import Spacer from '../../../../components/Spacer';
import {
  ChargeType,
  NormalText,
  PageTitle,
} from '../../../../components/typography/Typography';
import { Buttons } from '../Buttons';
import { useToastDispatch } from '../../../../components/Toast/ToastProvider';
import Dropdown, {
  DropdownOptionType,
} from '../../../../components/input/Dropdown';
import PriceInput from '../../../../components/input/PriceInput';
import Label from '../../../../components/input/Label';
import { createFragmentContainer, graphql } from 'react-relay';
import { AddCurrency_service } from './__generated__/AddCurrency_service.graphql';
import Table from '../../../../components/Table/Table';
import { mutationsAddCurrencyMutationVariables } from '../../../../api/__generated__/mutationsAddCurrencyMutation.graphql';

const Pricing = styled.div`
  display: flex;
  flex-direction: column;
`;

const Container = styled.div`
  display: flex;
  justify-content: center;
  flex: 1;
`;

type Props = {
  service: AddCurrency_service;
};

function AddCurrency({ service }: Props) {
  console.log('sup', service);
  const canAddCurrencies = service.supportableCurrencies.filter(
    (c) => !service.supportedCurrencies.includes(c)
  );
  const currencies = canAddCurrencies.map((c) => ({ label: c, value: c }));

  const [formState, setFormState] = useState<{
    prices: {
      price: number;
      chargeID: string;
    }[];
    currency: DropdownOptionType;
  }>({
    prices:
      service?.charges?.edges
        ?.map((service) => service?.node)
        .map((charge) => ({ price: 0, chargeID: charge?.id ?? '' })) ?? [],
    currency: currencies[0],
  });

  const { router, match } = useRouter();
  const dispatch = useToastDispatch();

  // onClose go back to previous page
  const onClose = () => {
    router.go(-1);
  };

  const onSave = async () => {
    // Check prices are valid

    for (let i of formState.prices) {
      if (isNaN(i.price)) {
        dispatch({
          text: 'Enter a valid price for all charges',
          alert: 'error',
        });
        return;
      }
    }

    const variables: mutationsAddCurrencyMutationVariables = {
      input: {
        serviceID: match.params.serviceID,
        newPrices: formState.prices,
        currency: formState.currency.value,
      },
    };

    try {
      await makeMutation(AddCurrencyMutation, variables);
      dispatch({
        text: `${formState.currency.label} added to API`,
        alert: 'success',
      });
      onClose();
    } catch (err) {
      dispatch({ text: err.toString(), alert: 'error', stayFor: 4000 });
    }
  };

  const priceFromChargeID = (id: string) =>
    formState.prices.find((price) => {
      return price.chargeID === id;
    });

  const updatePrice = (chargeID: string, price: number) => {
    setFormState((current) => {
      const newState = { ...current };
      for (const i of newState.prices) {
        if (i.chargeID === chargeID) {
          i.price = price;
        }
      }
      return newState;
    });
  };
  console.log('state', formState);
  return (
    <Container>
      <ContentCenterer maxWidth={'838px'}>
        <Spacer spacing={4} vertical />
        <PageTitle>Add a currency</PageTitle>
        <Spacer vertical spacing={2} />
        <NormalText>
          Add another currency to your API to create charges for users in their
          native currency.
        </NormalText>
        <Spacer vertical spacing={4} />
        <Dropdown
          label={'CURRENCY'}
          options={currencies}
          defaultOption={currencies[0]}
          onChange={(option) =>
            setFormState((val) => ({
              ...val,
              currency: option,
            }))
          }
        />
        <Spacer vertical spacing={4} />
        <Label>PRICING</Label>
        <Spacer vertical spacing={1} />
        <NormalText>
          Update the pricing of your current charges to include your new
          currency.
        </NormalText>
        <Spacer vertical spacing={2} />
        <Pricing>
          {/* <PriceInput
            value={formState.prices}
            onChange={(option) =>
              setFormState((val) => ({ ...val, price: option }))
            }
          /> */}
          <Table
            noHover
            rows={
              service?.charges?.edges?.map((service) => service?.node) ?? []
            }
            keyFunction={(row) => row.id}
            columns={[
              {
                headerName: 'Name',
                field: 'name',
                flex: 0.5,
              },
              {
                headerName: 'Identifier',
                component: (item) => item.chargeIdentifier,
                flex: 0.5,
              },
              {
                headerName: 'Type',
                flex: 0.5,
                component: (item) => <ChargeType>{item.chargeType}</ChargeType>,
              },
              {
                headerName: 'Price for new currency',
                flex: 1,
                component: (item) => (
                  <PriceInput
                    currency={formState.currency.value}
                    value={priceFromChargeID(item.id)?.price ?? undefined}
                    onChange={(val) => updatePrice(item.id, val)}
                  />
                ),
              },
            ]}
          />
        </Pricing>
        <Spacer vertical spacing={4} />
        <Buttons>
          <Button background={'#bab8c5'} color={'white'} onClick={onClose}>
            Cancel
          </Button>
          <Button color={'white'} onClick={onSave}>
            Add Currency
          </Button>
        </Buttons>
        <Spacer vertical spacing={4} />
      </ContentCenterer>
    </Container>
  );
}

export default createFragmentContainer(AddCurrency, {
  service: graphql`
    fragment AddCurrency_service on Service {
      id
      supportedCurrencies
      supportableCurrencies
      charges {
        edges {
          node {
            id
            name
            chargeIdentifier
            chargeType
            prices {
              amount
              currency
            }
          }
        }
      }
    }
  `,
});
